import { useRef } from "react";

// Full-batch photo capture — ported from legacy templates/workflow.html's
// photo-slot markup (static/css/app.css's .photo-slot rules), minus the AI
// OCR/QA call: AI is off in this environment, so this is capture + resumable
// upload (see lib/photos.ts) with manual grams entry alongside it, not an
// auto-read. `capture="environment"` opens the back camera directly on a
// phone; on desktop it just falls back to a normal file picker.

export type PhotoSlotState = "empty" | "uploading" | "queued" | "done" | "error";

export type PhotoField = { id: string | null; status: PhotoSlotState; previewUrl: string | null };

export function emptyPhotoField(): PhotoField {
  return { id: null, status: "empty", previewUrl: null };
}

const CAPTION: Partial<Record<PhotoSlotState, string>> = {
  uploading: "Uploading…",
  queued: "Queued — will upload when online",
  error: "Upload failed — will retry",
};

export function PhotoSlot({
  testId, label, hint, field, onCapture, onRetry,
}: {
  testId: string;
  label: string;
  hint: string;
  field: PhotoField;
  onCapture: (file: File) => void;
  onRetry: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const caption = CAPTION[field.status];

  return (
    <>
      <div
        className={`photo-slot${field.previewUrl ? " has-photo" : ""}`}
        data-testid={testId}
        data-state={field.status}
        data-photo-id={field.id ?? undefined}
        onClick={() => {
          if (!field.previewUrl) inputRef.current?.click();
        }}
      >
        {field.previewUrl ? (
          <>
            <img src={field.previewUrl} alt="scale" />
            <button
              type="button" className="retry"
              onClick={(e) => {
                e.stopPropagation();
                onRetry();
              }}
            >
              Retake
            </button>
            {caption && <div className="caption-over">{caption}</div>}
          </>
        ) : (
          <>
            <div>{label}</div>
            <div className="small muted">{hint}</div>
          </>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden-input"
        data-testid={`${testId}-input`}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) onCapture(file);
        }}
      />
    </>
  );
}
